import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Space } from 'antd';

import ComposerAttachmentTags from './ComposerAttachmentTags.jsx';
import ComposerActionRow from './ComposerActionRow.jsx';
import ComposerChooser from './ComposerChooser.jsx';
import { MODE_KEYS, normalizeQuickModeEntries } from './composerShared.jsx';
import useComposerChooser from './useComposerChooser.js';
import useComposerAbortHotkey from './useComposerAbortHotkey.js';
import useComposerModeHotkeys from './useComposerModeHotkeys.js';

export {
  getAttachmentDisplayText,
  isSendTriggerEnabled,
  resolveSendDisabledState,
} from './composerShared.jsx';

import { CHAT_COMPOSER_DEFAULTS, conditionalValue, normalizeTargetProps, composerBoxClassName } from "./composerLayout.js";
export default function ChatComposer(props) {
  const {
  value,
  disabled,
  sendDisabled,
  loading,
  submitting,
  placeholder,
  sendLabel,
  stopLabel,
  hintText,
  attachLabel,
  fileList,
  onChange,
  onSend,
  onAbort,
  onOpenSkillManagement,
  onAttachFiles,
  onRemoveFile,
  modeOptions,
  modeValue,
  onModeChange,
  composerActions,
  composerChooser,
  modeStatusByKey,
  shortcutAction,
  shortcutActions,
  onComposerActionSelect,
  onComposerChooserSubmit,
  onComposerChooserDismiss,
  inputRef,
  themeTokens,
  embedded,
  attachmentConstraintsHint,
  activeTargetChip,
  onActiveTargetDismiss,
  } = { ...CHAT_COMPOSER_DEFAULTS, ...normalizeTargetProps(props) };
  void hintText;
  const fileInputRef = useRef(null);
  const modeButtonAnimationTimerRef = useRef(null);
  const attachmentDragDepthRef = useRef(0);
  const [toolMenuOpen, setToolMenuOpen] = useState(false);
  const [attachmentDragActive, setAttachmentDragActive] = useState(false);
  const [modeButtonAnimatingKey, setModeButtonAnimatingKey] = useState('');
  const quickModeEntries = useMemo(() => normalizeQuickModeEntries(modeOptions), [modeOptions]);
  const isBusy = [loading, submitting].some(Boolean);

  const {
    chooserAllowsOtherInput,
    chooserCanSubmit,
    chooserConfirmOnSelect,
    chooserCustomInput,
    chooserHideSubmit,
    chooserInlineInputRef,
    chooserOptions,
    chooserOtherInputSelected,
    chooserPages,
    chooserPrimaryActionLabel,
    chooserRecommendedKey,
    chooserRef,
    chooserSelectedKey,
    chooserSelectedKeys,
    chooserSelectionMode,
    chooserSubmitHotkey,
    chooserTotalPages,
    currentChooserPage,
    handleChooserDismiss,
    handleChooserKeyDown,
    handleChooserSubmit,
    hasComposerChooser,
    normalizedChooserPage,
    setChooserCustomInput,
    setChooserOtherInputSelected,
    setChooserPage,
    setChooserSelectedKey,
    setChooserSelectedKeys,
  } = useComposerChooser({
    composerChooser,
    sendDisabled: sendDisabled || isBusy,
    onComposerActionSelect,
    onComposerChooserSubmit,
    onComposerChooserDismiss,
  });

  const { handleInputKeyDown } = useComposerModeHotkeys({
    composerActions,
    shortcutAction,
    shortcutActions,
    quickModeEntries,
    hasComposerChooser,
    modeValue,
    onModeChange,
    onComposerActionSelect,
    disabled,
    loading: isBusy,
    sendDisabled,
    onSend,
  });
  useComposerAbortHotkey({ loading, submitting, onAbort });

  const handleFileSelect = useCallback((event) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length > 0) {
      onAttachFiles?.(selectedFiles);
    }
    event.target.value = '';
  }, [onAttachFiles]);

  const handleAttachmentDragState = useCallback((event, type) => {
    if (disabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const dataTransfer = Object.assign({}, event.dataTransfer);
    const hasFiles = Array.from(dataTransfer.types ?? []).includes('Files');
    if (!hasFiles) {
      return;
    }
    if (type === 'enter') {
      attachmentDragDepthRef.current += 1;
      setAttachmentDragActive(true);
      return;
    }
    if (type === 'over') {
      event.dataTransfer.dropEffect = 'copy';
      setAttachmentDragActive(true);
      return;
    }
    attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
    if (attachmentDragDepthRef.current === 0) {
      setAttachmentDragActive(false);
    }
  }, [disabled]);

  const handleAttachmentDrop = useCallback((event) => {
    if (disabled) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    attachmentDragDepthRef.current = 0;
    setAttachmentDragActive(false);
    const dataTransfer = Object.assign({}, event.dataTransfer);
    const droppedFiles = Array.from(dataTransfer.files ?? []);
    if (droppedFiles.length > 0) {
      onAttachFiles?.(droppedFiles);
    }
  }, [disabled, onAttachFiles]);

  const isIntakeMode = modeValue === MODE_KEYS.intake;
  const isAnalysisMode = modeValue === MODE_KEYS.analysis;

  const handleModeToggle = useCallback((targetMode) => {
    if (!targetMode || modeValue === targetMode) {
      return;
    }
    onModeChange?.(targetMode);
  }, [modeValue, onModeChange]);

  const handleModeSwitchChange = useCallback((targetMode, checked) => {
    if (!targetMode) {
      return;
    }
    const nextMode = checked ? targetMode : MODE_KEYS.auto;
    if (modeValue === nextMode) {
      return;
    }
    onModeChange?.(nextMode);
  }, [modeValue, onModeChange]);

  useEffect(() => () => {
    clearTimeout(modeButtonAnimationTimerRef.current);
  }, []);

  return (
    <div
      className="flare-composer"
      style={{
        background: conditionalValue(embedded, 'transparent', themeTokens.composerBg),
        borderTop: conditionalValue(embedded, 0, `1px solid ${themeTokens.divider}`),
        padding: conditionalValue(embedded, 0, 12),
      }}
    >
      <Space orientation="vertical" size={8} style={{ width: '100%' }}>
        <div
          className={composerBoxClassName(attachmentDragActive, isBusy)}
          onDragEnter={(event) => handleAttachmentDragState(event, 'enter')}
          onDragOver={(event) => handleAttachmentDragState(event, 'over')}
          onDragLeave={(event) => handleAttachmentDragState(event, 'leave')}
          onDrop={handleAttachmentDrop}
          style={{
            background: themeTokens.composerBoxBg,
            border: `1px solid ${themeTokens.surfaceBorder}`,
            borderRadius: 12,
            padding: 10,
          }}
        >
          <ComposerAttachmentTags
            attachmentConstraintsHint={attachmentConstraintsHint}
            fileList={fileList}
            onRemoveFile={onRemoveFile}
            themeTokens={themeTokens}
          />
          {conditionalValue(composerChooser?.presentation?.pending === true, (
            <div role="status" aria-live="polite">提交中</div>
          ))}
          {conditionalValue(hasComposerChooser, (
            <ComposerChooser
              disabled={sendDisabled}
              loading={loading}
              submitting={submitting}
              chooserAllowsOtherInput={chooserAllowsOtherInput}
              chooserCanSubmit={chooserCanSubmit}
              chooserConfirmOnSelect={chooserConfirmOnSelect}
              chooserCustomInput={chooserCustomInput}
              chooserHideSubmit={chooserHideSubmit}
              chooserInlineInputRef={chooserInlineInputRef}
              chooserOptions={chooserOptions}
              chooserOtherInputSelected={chooserOtherInputSelected}
              chooserPages={chooserPages}
              chooserPrimaryActionLabel={chooserPrimaryActionLabel}
              chooserRecommendedKey={chooserRecommendedKey}
              chooserRef={chooserRef}
              chooserSelectedKey={chooserSelectedKey}
              chooserSelectedKeys={chooserSelectedKeys}
              chooserSelectionMode={chooserSelectionMode}
              chooserSubmitHotkey={chooserSubmitHotkey}
              chooserTotalPages={chooserTotalPages}
              composerChooser={composerChooser}
              currentChooserPage={currentChooserPage}
              handleChooserDismiss={handleChooserDismiss}
              handleChooserKeyDown={handleChooserKeyDown}
              handleChooserSubmit={handleChooserSubmit}
              normalizedChooserPage={normalizedChooserPage}
              setChooserCustomInput={setChooserCustomInput}
              setChooserOtherInputSelected={setChooserOtherInputSelected}
              setChooserPage={setChooserPage}
              setChooserSelectedKey={setChooserSelectedKey}
              setChooserSelectedKeys={setChooserSelectedKeys}
            />
          ), (
            <>
              <div className="flare-composer-input-wrap">
                {conditionalValue(isBusy, (
                  <div className="flare-composer-loading-placeholder" aria-live="polite">
                    <span>{conditionalValue(submitting, '发送中', '生成中')}</span>
                    <span className="flare-composer-loading-dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  </div>
                ), null)}
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  className="flare-composer-input"
                  disabled={[disabled, loading, submitting].some(Boolean)}
                  onChange={onChange}
                  onKeyDown={handleInputKeyDown}
                  placeholder={conditionalValue(isBusy, '', placeholder)}
                  ref={inputRef}
                  style={{ background: 'transparent', color: themeTokens.inputText }}
                  value={value}
                  variant="borderless"
                />
              </div>
              <ComposerActionRow
                attachLabel={attachLabel}
                disabled={disabled}
                fileInputRef={fileInputRef}
                handleFileSelect={handleFileSelect}
                handleModeSwitchChange={handleModeSwitchChange}
                handleModeToggle={handleModeToggle}
                isAnalysisMode={isAnalysisMode}
                isIntakeMode={isIntakeMode}
                loading={loading}
                modeButtonAnimatingKey={modeButtonAnimatingKey}
                modeButtonAnimationTimerRef={modeButtonAnimationTimerRef}
                modeStatusByKey={modeStatusByKey}
                modeValue={modeValue}
                onAbort={onAbort}
                onOpenSkillManagement={onOpenSkillManagement}
                onModeChange={onModeChange}
                onSend={onSend}
                onTargetDismiss={onActiveTargetDismiss}
                quickModeEntries={quickModeEntries}
                sendDisabled={sendDisabled}
                sendLabel={sendLabel}
                setModeButtonAnimatingKey={setModeButtonAnimatingKey}
                setToolMenuOpen={setToolMenuOpen}
                stopLabel={stopLabel}
                submitting={submitting}
                targetChip={activeTargetChip}
                themeTokens={themeTokens}
                toolMenuOpen={toolMenuOpen}
              />
            </>
          ))}
        </div>
      </Space>
    </div>
  );
}
